import { useState, useEffect } from 'react'
import type { Patient } from '@alpha-edge/shared'
import {
  Modal,
  FormField,
  btnPrimary,
  btnSecondary,
  btnDanger,
  colors,
  input,
  ConfirmDialog,
} from '../../components/ui'
import { useModal } from '../../hooks/use-modal'
import { api, ApiError } from '../../services/api'
import { ConsentPanel } from './ConsentPanel.js'
import { useFeatures } from '../../hooks/use-features.js'

interface PatientForm {
  firstName: string
  lastName: string
  middleName: string
  dateOfBirth: string
  sex: string
  medicalRecordNumber: string
  externalId: string
  email: string
  phone: string
  street: string
  city: string
  state: string
  zip: string
  country: string
}

const defaults: PatientForm = {
  firstName: '',
  lastName: '',
  middleName: '',
  dateOfBirth: '',
  sex: 'unknown',
  medicalRecordNumber: '',
  externalId: '',
  email: '',
  phone: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
}

interface PatientEditModalProps {
  readonly patientId: string | null
  readonly isOpen: boolean
  readonly isNew: boolean
  readonly onClose: () => void
  readonly onSaved: (patient: Patient) => void
  readonly onArchived?: (patientId: string) => void
}

function patientToForm(p: Patient): PatientForm {
  return {
    firstName: p.firstName,
    lastName: p.lastName,
    middleName: p.middleName ?? '',
    dateOfBirth: p.dateOfBirth,
    sex: p.sex,
    medicalRecordNumber: p.medicalRecordNumber ?? '',
    externalId: p.externalId ?? '',
    email: p.email ?? '',
    phone: p.phone ?? '',
    street: p.street ?? '',
    city: p.city ?? '',
    state: p.state ?? '',
    zip: p.zip ?? '',
    country: p.country ?? 'US',
  }
}

export function PatientEditModal({ patientId, isOpen, isNew, onClose, onSaved, onArchived }: PatientEditModalProps) {
  const features = useFeatures()
  const [form, setForm] = useState<PatientForm>(defaults)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    if (isNew) {
      setForm(defaults)
      setError(null)
      return
    }
    if (!patientId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get<Patient>(`/patients/${patientId}`)
      .then((res) => {
        if (cancelled) return
        if (res.data) setForm(patientToForm(res.data))
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load patient')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [patientId, isOpen, isNew])

  const update = <K extends keyof PatientForm>(key: K, value: PatientForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const payload: Record<string, unknown> = { ...form }
      // Clean up empty optional strings so backend treats them as null
      for (const key of [
        'middleName',
        'medicalRecordNumber',
        'externalId',
        'email',
        'phone',
        'street',
        'city',
        'state',
        'zip',
      ] as const) {
        if (payload[key] === '') delete payload[key]
      }
      const res = isNew
        ? await api.post<Patient>('/patients', payload)
        : await api.put<Patient>(`/patients/${patientId}`, payload)
      if (!res.data) throw new Error('No data returned')
      onSaved(res.data)
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive() {
    if (!patientId) return
    setArchiving(true)
    try {
      await api.delete(`/patients/${patientId}`)
      onArchived?.(patientId)
      archiveModal.close()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to archive')
    } finally {
      setArchiving(false)
    }
  }

  const title = isNew ? 'New Patient' : `${form.lastName}, ${form.firstName}`

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={title} width="720px">
        {loading ? (
          <div style={{ color: colors.textMuted, padding: '1rem 0' }}>Loading...</div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
              <FormField
                name="firstName"
                label="First Name"
                required
                value={form.firstName}
                onChange={(v) => update('firstName', v)}
              />
              <FormField
                name="lastName"
                label="Last Name"
                required
                value={form.lastName}
                onChange={(v) => update('lastName', v)}
              />
              <FormField
                name="middleName"
                label="Middle Name"
                value={form.middleName}
                onChange={(v) => update('middleName', v)}
              />
              <FormField
                name="dateOfBirth"
                label="Date of Birth"
                required
                type="date"
                value={form.dateOfBirth}
                onChange={(v) => update('dateOfBirth', v)}
              />
              <FormField name="sex" label="Sex" required>
                <select style={input} value={form.sex} onChange={(e) => update('sex', e.target.value)}>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="unknown">Unknown</option>
                </select>
              </FormField>
              <FormField
                name="medicalRecordNumber"
                label="MRN"
                value={form.medicalRecordNumber}
                onChange={(v) => update('medicalRecordNumber', v)}
              />
              <FormField
                name="externalId"
                label="External ID"
                value={form.externalId}
                onChange={(v) => update('externalId', v)}
              />
              <FormField
                name="email"
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => update('email', v)}
              />
              <FormField name="phone" label="Phone" value={form.phone} onChange={(v) => update('phone', v)} />
            </div>

            <div
              style={{
                gridColumn: '1 / -1',
                borderTop: `1px solid ${colors.border}`,
                paddingTop: '0.75rem',
                marginTop: '0.75rem',
              }}
            >
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.9375rem', fontWeight: 600, color: colors.text }}>
                Address
              </h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '0.75rem' }}>
              <FormField name="street" label="Street" value={form.street} onChange={(v) => update('street', v)} />
              <FormField name="city" label="City" value={form.city} onChange={(v) => update('city', v)} />
              <FormField name="state" label="State" value={form.state} onChange={(v) => update('state', v)} />
              <FormField name="zip" label="ZIP" value={form.zip} onChange={(v) => update('zip', v)} />
            </div>

            {!isNew && patientId && features.hipaa && <ConsentPanel patientId={patientId} />}

            {error && (
              <div
                style={{
                  marginTop: '0.75rem',
                  color: colors.danger,
                  padding: '0.5rem 0.75rem',
                  background: colors.bgError,
                  border: `1px solid ${colors.borderError}`,
                  borderRadius: '3px',
                  fontSize: '0.875rem',
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
              {!isNew && onArchived && (
                <button
                  type="button"
                  onClick={archiveModal.open}
                  style={{ ...btnDanger, marginRight: 'auto' }}
                  disabled={saving || archiving}
                >
                  Archive
                </button>
              )}
              <button type="button" onClick={onClose} style={btnSecondary} disabled={saving}>
                Cancel
              </button>
              <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Patient"
        message={`Are you sure you want to archive ${form.firstName} ${form.lastName}? This patient will be excluded from search results.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </>
  )
}
